// Recover a stale session whose lazy chunks 404 after a deploy/promote (ENG-1569).
//
// When production is promoted to a new build, hashed asset filenames rotate. A
// session (or edge-cached index.html) still referencing old hashes 404s on
// import; reloading picks up the fresh index.html + current hashes.
//
// This module must be imported BEFORE the app (`./App`) so the listener is
// registered before the router's eager `SignIn.preload()` fires its dynamic
// import at module-eval time.
//
// The previous guard keyed on the error *message* in a single slot, so a broken
// boot — where several distinct chunks fail (SignIn, CreateAccount, CSS, vendor)
// — kept overwriting the slot and reloaded on every differing message: an
// infinite reload loop. This guard caps total reloads and ignores the burst of
// sibling failures within one page life, so we reload at most twice and then
// stop rather than loop.
//
// The reload count MUST survive a reload or the cap can't be enforced — and an
// uncapped reload *is* the loop. `sessionStorage` is unreliable for this: it can
// throw a SecurityError (locked-down/private modes) or silently no-op (privacy
// shields/extensions), either of which loses the count. `window.name` persists
// across same-tab reloads with no storage permission and is unused elsewhere in
// the app, so it's the durable backstop; we mirror to sessionStorage and read
// the max so whichever persisted wins (covers both the throw and the silent
// no-op cases).
//
// ENG-1638: `event.preventDefault()` must ONLY be called on the path that
// actually reloads. Vite resolves a preventDefault'ed import with `undefined`
// instead of rejecting it, so suppressing the error without reloading makes
// React.lazy read `undefined.default` and throw the misleading
// `Cannot read properties of undefined (reading 'default')` on every lazy
// route render — exactly what sessions stuck at the reload cap were reporting
// to Datadog. When we don't reload, we let the real import error propagate:
// the route's errorElement still catches it, and Error Tracking sees the true
// chunk failure instead of a mis-grouped TypeError.

const RELOAD_COUNT_KEY = 'chunk-reload-count'
const NAME_PREFIX = '__chunkReload='
const MAX_CHUNK_RELOADS = 2
let reloadTriggeredThisLife = false

const readFromSession = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0
  } catch {
    return 0
  }
}

const readFromName = (): number =>
  window.name.startsWith(NAME_PREFIX)
    ? Number(window.name.slice(NAME_PREFIX.length)) || 0
    : 0

export const getReloadCount = (): number =>
  Math.max(readFromName(), readFromSession())

export const setReloadCount = (count: number): void => {
  // window.name is verified unused in the app — safe to own as the backstop.
  window.name = NAME_PREFIX + count
  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count))
  } catch {
    // window.name is the source of truth when storage is unavailable.
  }
}

export const clearReloadCount = (): void => {
  if (window.name.startsWith(NAME_PREFIX)) window.name = ''
  try {
    sessionStorage.removeItem(RELOAD_COUNT_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

// Exported for tests; `reload` is injectable because jsdom does not implement
// (or reliably allow stubbing of) `window.location.reload`.
export const handleChunkPreloadError = (
  event: Event,
  reload: () => void = () => window.location.reload()
): void => {
  // Many chunks can fail near-simultaneously on a single broken boot; only the
  // first should trigger a reload. A reload is already in flight — let the
  // sibling failures reject naturally; the page is torn down momentarily.
  if (reloadTriggeredThisLife) return

  if (getReloadCount() >= MAX_CHUNK_RELOADS) {
    // Already retried and still failing (e.g. a stale edge-cached index.html).
    // Stop reloading — and do NOT preventDefault: the real import error must
    // surface to the route error boundary and Datadog (see ENG-1638 note).
    return
  }

  // We own recovery via reload — suppress the error only here, where the page
  // is about to be replaced anyway.
  event.preventDefault()
  reloadTriggeredThisLife = true
  setReloadCount(getReloadCount() + 1)
  reload()
}

window.addEventListener('vite:preloadError', event =>
  handleChunkPreloadError(event)
)

// Once the app boots cleanly (no preload error triggered a reload), reset the
// budget so a future, unrelated deploy starts fresh instead of stuck at the cap.
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!reloadTriggeredThisLife) clearReloadCount()
  }, 5000)
})
